/**
 * Datetime helpers for the rule builder + node config forms.
 *
 * The browser's <input type="datetime-local"> control wants
 * "YYYY-MM-DDTHH:mm" with no timezone (treated as local). FlowDefinition
 * stores datetimes as full ISO 8601 strings (UTC). These helpers bridge
 * the two so node forms can use the native picker without losing precision
 * or timezone correctness in the saved JSON.
 */

const pad2 = (n: number) => n.toString().padStart(2, "0");

/** "2026-04-22T15:30:00.000Z" → "2026-04-22T15:30" (in the user's local time). */
export function isoToLocalInput(iso: string): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

/** "2026-04-22T15:30" (local) → "2026-04-22T15:30:00.000Z" (UTC ISO). */
export function localInputToIso(local: string): string {
  if (!local) return "";
  const d = new Date(local);
  if (Number.isNaN(d.getTime())) return local;
  return d.toISOString();
}

// ─── ISO 8601 duration ↔ {amount, unit} ──────────────────────────────────────

export type DurationUnit = "minutes" | "hours" | "days";

export interface ParsedDuration {
  amount: number;
  unit: DurationUnit;
}

/**
 * Parses simple ISO 8601 durations of the form PT{n}M, PT{n}H, P{n}D into
 * {amount, unit}. Returns null for anything more exotic (mixed periods,
 * weeks, etc.) so the caller can fall back to a raw text input.
 */
export function parseDuration(iso: string): ParsedDuration | null {
  if (!iso) return null;
  const m = iso.match(/^P(?:T(\d+)([MH])|(\d+)D)$/);
  if (!m) return null;
  if (m[1] && m[2]) {
    return { amount: Number(m[1]), unit: m[2] === "H" ? "hours" : "minutes" };
  }
  if (m[3]) {
    return { amount: Number(m[3]), unit: "days" };
  }
  return null;
}

export function formatDuration({ amount, unit }: ParsedDuration): string {
  switch (unit) {
    case "minutes":
      return `PT${amount}M`;
    case "hours":
      return `PT${amount}H`;
    case "days":
      return `P${amount}D`;
  }
}
